import React, { FC } from 'react';
import { RootState, useSelector } from 'src/store';
import { changeEnterHTML } from 'src/utils/changeEnter';
import { getFontURL } from 'src/utils/getFontUrl';
import { trackingList } from 'src/utils/valueHTML';

const BasicFullRouletteHTML: FC = () => {
  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);
  const deeplink = useSelector(
    (state: RootState) => state.imageUpload.deeplink
  );
  const radius = useSelector((state: RootState) => state.imageUpload.radius);

  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);

  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const tracking = useSelector((state: RootState) => state.trackings.tracking);
  const button1 = useSelector((state: RootState) => state.trackings.button1);
  const button2 = useSelector((state: RootState) => state.trackings.button2);

  const titleFontURL = getFontURL(fontFamily?.title);
  const subTitleFontURL = getFontURL(fontFamily?.sub_title);
  const btnFontURL = getFontURL(btnFontFamily?.result_button);
  const startBtnFontURL = getFontURL(btnFontFamily?.start_button);
  const rouletteItemsFontURL = getFontURL(fontFamily?.roulette_items);
  const resultTextFontURL = getFontURL(fontFamily?.result_text);
  const resultSubTextFontURL = getFontURL(fontFamily?.result_sub_text);

  // 배경 투명도 계산
  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.7;

  const style = `
    @font-face {
      font-family: 'titleFont';
      src: url(${titleFontURL});
    }
    @font-face {
      font-family: 'subTitleFont';
      src: url(${subTitleFontURL});
    }
    @font-face {
      font-family: 'btnFont';
      src: url(${btnFontURL});
    }
    @font-face {
      font-family: 'startBtnFont';
      src: url(${startBtnFontURL});
    }
    @font-face {
      font-family: 'rouletteItemsFont';
      src: url(${rouletteItemsFontURL});
    }
    @font-face {
      font-family: 'resultTextFont';
      src: url(${resultTextFontURL});
    }
    @font-face {
      font-family: 'resultSubTextFont';
      src: url(${resultSubTextFontURL});
    }

    /* reset */
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      list-style: none;
      text-decoration: none;
    }
    html,
    body {
      height: 100%;
      overflow: hidden;
    }
    main {
      height: 100%;
      position: relative;
    }
    a {
      color: #000000;
      cursor: pointer;
    }
    button {
      background: none;
      border: none;
    }

    /* custom */

    .root-view {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, ${displayValue});
    }

    canvas {
      width: 350px;
      height: 350px;
      background-color: transparent;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      -webkit-user-select: none;
      border: none;
    }

    .roulette-container {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 400px;
      height: 100%;
      padding: 20px;
      z-index: 1;
    }

    .header {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: auto;
      margin-bottom: 20px;
      gap: 5px;
    }

    .title {
      width: 100%;
      height: auto;
      max-height: 48px;
      line-height: 1.2;
      font-size: ${fontSize?.title}rem;
      font-weight: ${fontWeight?.title};
      color: ${color?.title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.title
        )
          ? fontFamily?.title
          : 'titleFont'
      } !important;
      text-align: ${textAlign?.title};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      display: -webkit-box;
    }

    .sub-title {
      width: 100%;
      height: auto;
      min-height: 80px;
      line-height: 1.4;
      font-size: ${fontSize?.sub_title}rem;
      font-weight: ${fontWeight?.sub_title};
      color: ${color?.sub_title};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.sub_title
        )
          ? fontFamily?.sub_title
          : 'subTitleFont'
      } !important;
      text-align: ${textAlign?.sub_title};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
      overflow: hidden;
    }

    .close-btn {
      position: absolute;
      top: 6%;
      right: 10%;
      background: none;
      border: none;
      color: #ffffff;
      width: 24px;
      height: 24px;
      font-size: 24px;
      cursor: pointer;
      z-index: 10;
    }

    .roulette-wrapper {
      position: relative;
      display: flex;
      justify-content: center;
      width: 100%;
      height: fit-content;
    }

    .pin {
      position: absolute;
      top: -15px;
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 20px solid #ffffff;
      z-index: 1;
    }

    @keyframes fadeInBackground {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes slideUpModal {
      from {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .modal-background {
      position: absolute;
      top: 0;
      left: 0;
      display: none;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      background-color: transparent;
      z-index: 2;
      padding: 0 20px;
    }

    .modal-background.show {
      display: flex;
      animation: fadeInBackground 0.6s ease-out;
    }

    .modal-content {
      width: 100%;
      max-width: 340px;
      padding: 26px 20px 20px 20px;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      border-radius: ${radius?.result_modal_bg}px;
      background-color: ${bgColor?.result_modal_bg};
    }

    .modal-background.show .modal-content {
      animation: slideUpModal 0.6s ease-out;
    }

    .result-text {
      width: 100%;
      height: auto;
      line-height: 1.4;
      font-size: ${fontSize?.result_text}rem;
      font-weight: ${fontWeight?.result_text};
      color: ${color?.result_text};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.result_text
        )
          ? fontFamily?.result_text
          : 'resultTextFont'
      } !important;
      text-align: ${textAlign?.result_text};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
      overflow: hidden;
      margin-bottom: 7px;
    }

    .result-sub-text {
      width: 100%;
      height: auto;
      line-height: 1.54;
      font-size: ${fontSize?.result_sub_text}rem;
      font-weight: ${fontWeight?.result_sub_text};
      color: ${color?.result_sub_text};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.result_sub_text
        )
          ? fontFamily?.result_sub_text
          : 'resultSubTextFont'
      } !important;
      text-align: ${textAlign?.result_sub_text};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
      overflow: hidden;
    }

    .result-icon {
      width: 100%;
      text-align: center;
      margin: 10px 0;
    }

    .result-image {
      width: 224px;
      height: 224px;
      border-radius: ${radius?.result_image}px;
      object-fit: cover;
    }

    .result-btn {
      width: 100%;
      height: 48px;
      outline: 0;
      border: 0;
      padding: 0 16px;
      margin-top: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: ${btnFontWeight?.result_button};
      font-size: ${btnFontSize?.result_button}rem;
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.result_button
        )
          ? btnFontFamily?.result_button
          : 'btnFont'
      };
      color: ${btnColor?.result_button};
      border-radius: ${radius?.result_button}px;
      background-color:rgba(${btnBgColorR?.result_button},${
    btnBgColorG?.result_button
  },${btnBgColorB?.result_button}, ${btnBgColorA?.result_button});
    }
  `;

  const deeplink_list = JSON.stringify([deeplink?.result_button]);

  const html = `
    <div class="root-view">
      <div class="roulette-container">
        <div class="close-btn">✕</div>

        <div class="header">
          <div class="title">${changeEnterHTML(text?.title)}</div>
          <div class="sub-title">${changeEnterHTML(text?.sub_title)}</div>
        </div>

        <div class="roulette-wrapper">
          <div class="pin"></div>
          <canvas id="rouletteCanvas"></canvas>
        </div>

        <div class="modal-background" id="modalBackground">
          <div class="modal-content">
            <div class="result-text" id="resultText">${changeEnterHTML(
              text?.result_text
            )}</div>
            <div class="result-sub-text" id="resultSubText">${changeEnterHTML(
              text?.result_sub_text
            )}</div>
            ${
              bgImage?.result_image
                ? `<div class="result-icon"><img src="${bgImage.result_image}" alt="result" class="result-image" /></div>`
                : ''
            }
            <button class="result-btn">${btnText?.result_button}</button>
          </div>
        </div>
      </div>
      <div id="deeplink" style="display:none;">
        ${deeplink_list}
      </div>
    </div>
  `;

  let tracking_list = '[';
  for (let i = 0; i < tracking?.background?.length; i++) {
    tracking_list += `['${tracking?.background?.[i]}', '${tracking?.start_button?.[i]}', '${tracking?.result_button?.[i]}', '${tracking?.close_button?.[i]}'],`;
  }
  tracking_list += ']';

  const tracking_button_list = trackingList(
    'BASIC FULL ROULETTE',
    button1,
    button2
  );

  // 룰렛 아이템 동적 생성
  const rouletteItems = text?.roulette_items;

  // 중앙 버튼 색상 (start_button)
  const centerButtonColor = `rgba(${btnBgColorR?.start_button ??
    255}, ${btnBgColorG?.start_button ?? 107}, ${btnBgColorB?.start_button ??
    107}, ${btnBgColorA?.start_button ?? 1})`;
  const centerButtonTextColor = btnColor?.start_button;

  // 룰렛 아이템 스타일
  const rouletteItemColor = color?.roulette_items;
  const rouletteItemFontSize = fontSize?.roulette_items;
  const rouletteItemFontWeight = fontWeight?.roulette_items;
  const rouletteItemFontFamily = [
    'Roboto',
    'Inter',
    'Montserrat',
    'Poppins',
    'Pretendard'
  ].includes(fontFamily?.roulette_items)
    ? fontFamily?.roulette_items
    : 'rouletteItemsFont';

  // start_button 스타일
  const startButtonFontSize = btnFontSize?.start_button;
  const startButtonFontWeight = btnFontWeight?.start_button;
  const startButtonFontFamily = [
    'Roboto',
    'Inter',
    'Montserrat',
    'Poppins',
    'Pretendard'
  ].includes(btnFontFamily?.start_button)
    ? btnFontFamily?.start_button
    : 'startBtnFont';

  const js = `
  document.addEventListener("DOMContentLoaded", function(){
    const content = document.getElementById("deeplink").textContent;
    const link = content.replace(/\\n|\\t/g, "").trim();
    const deeplink = JSON.parse(link);

    let tracking = ${tracking_list};
    let button1 = ${tracking_button_list[0]};
    let button2 = ${tracking_button_list[1]};

    // Roulette data
    const items = ${JSON.stringify(rouletteItems)};

    const canvas = document.getElementById('rouletteCanvas');
    const ctx = canvas.getContext('2d');
    let currentRotation = -Math.PI / 2;
    let isSpinning = false;

    // Device Pixel Ratio for high-resolution displays
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = canvas.clientWidth || 350;
    const displayHeight = canvas.clientHeight || 350;

    // Set canvas internal resolution
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Scale context for DPR
    ctx.scale(dpr, dpr);

    // Colors
    const borderColor = '${centerButtonColor}';
    const centerBtnColor = '${centerButtonColor}';
    const centerBtnTextColor = '${centerButtonTextColor}';
    const sectorColor = '${bgColor?.roulette_items}';

    // Draw roulette (using logical size, not canvas.width/height)
    function drawRoulette() {
      const centerX = displayWidth / 2;
      const centerY = displayHeight / 2;
      const radius = Math.min(displayWidth, displayHeight) / 2 - 30;

      // Clear with DPR consideration
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      // Outer border
      ctx.save();
      ctx.translate(centerX, centerY);
      const outerRadius = radius + 15;
      ctx.beginPath();
      ctx.arc(0, 0, outerRadius, 0, Math.PI * 2);
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 8;
      ctx.stroke();

      // Draw dots
      for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI * 2) / 8;
        const x = Math.cos(angle) * outerRadius;
        const y = Math.sin(angle) * outerRadius;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      }
      ctx.restore();

      // Draw slices
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(currentRotation);

      const sliceAngle = (2 * Math.PI) / items.length;
      const textDistance = radius * 0.6;

      items.forEach((item, i) => {
        const startAngle = i * sliceAngle;

        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, radius, startAngle, startAngle + sliceAngle);
        ctx.closePath();
        ctx.fillStyle = sectorColor;
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 0.5;
        ctx.stroke();

        ctx.save();
        ctx.rotate(startAngle + sliceAngle / 2);
        ctx.fillStyle = '${rouletteItemColor}';
        ctx.font = '${rouletteItemFontWeight} ${(rouletteItemFontSize ||
    0.875) * 16}px ${rouletteItemFontFamily}, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.translate(textDistance, 0);
        ctx.rotate(Math.PI / 2);
        ctx.fillText(item, 0, -15);
        ctx.restore();
      });

      // Center button
      const centerRadius = radius * 0.3;
      ctx.beginPath();
      ctx.arc(0, 0, centerRadius, 0, Math.PI * 2);
      ctx.fillStyle = centerBtnColor;
      ctx.fill();

      ctx.save();
      ctx.rotate(-currentRotation);
      ctx.fillStyle = centerBtnTextColor;
      ctx.font = '${startButtonFontWeight} ${(startButtonFontSize || 1.125) *
    16}px ${startButtonFontFamily}, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('${btnText?.start_button}', 0, 0);
      ctx.restore();

      ctx.restore();
    }

    // Spin roulette
    function spinRoulette() {
      if (isSpinning) return;
      isSpinning = true;

      const startTime = performance.now();
      const spinDuration = 3000; // 3초
      const rotationsPerSecond = 1.5; // 초당 회전 수 (1.5바퀴/초)
      const radiansPerSecond = rotationsPerSecond * Math.PI * 2; // 라디안/초로 변환

      let lastTime = startTime;

      function animate(currentTime) {
        const elapsed = currentTime - startTime;
        const deltaTime = (currentTime - lastTime) / 1000; // 초 단위로 변환
        lastTime = currentTime;

        if (elapsed < spinDuration) {
          // 시간 기반으로 회전 (모든 기기에서 동일한 속도)
          currentRotation += radiansPerSecond * deltaTime;
          drawRoulette();
          requestAnimationFrame(animate);
        } else {
          // 3초 후 모달 표시 (어디에 멈췄는지 보여주지 않음)
          showResult();
          isSpinning = false;
        }
      }

      requestAnimationFrame(animate);
    }

    // Show result modal
    function showResult() {
      document.getElementById('modalBackground').classList.add('show');
      document.querySelector('.header').style.visibility = 'hidden';
      document.querySelector('.roulette-wrapper').style.visibility = 'hidden';
    }

    // Close modal
    function closeModal() {
      document.getElementById('modalBackground').classList.remove('show');
      currentRotation = -Math.PI / 2;
      isSpinning = false;
      drawRoulette();
    }

    // Initial draw - 폰트 로드 완료 후 그리기
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function() {
        drawRoulette();
      });
    } else {
      drawRoulette();
    }

    // ========================================
    // 버튼 클릭 이벤트
    // tracking_list 순서: [0]background, [1]start_button, [2]button, [3]close_button
    // button1/button2 순서 (TEMPLATES_TRACKING_EDITOR): [0]start_button, [1]button, [2]close_button
    // ========================================

    // 1. START 버튼 클릭 (start_button) - 룰렛 시작
    // tracking: [1], button1/2: [0]
    canvas.addEventListener('click', function(e) {
      const rect = canvas.getBoundingClientRect();
      // Use CSS size for click coordinates (DPR independent)
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = displayWidth / 2;
      const centerY = displayHeight / 2;
      const radius = Math.min(displayWidth, displayHeight) / 2 - 30;
      const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));

      if (distance <= radius * 0.3) {
        appboyBridge.logClick(tracking[0][1]);
        try {
          if (button1[0].indexOf(0) !== -1) {
            appboyBridge.logClick('0');
          }
          if (button2[0].indexOf(0) !== -1) {
            appboyBridge.logClick('1');
          }
        } catch (err) {}
        spinRoulette();
      }
    });

    // 2. GET REWARD 버튼 클릭 (button) - 결과 모달에서 보상 받기
    // tracking: [2], button1/2: [1]
    document.querySelector('.result-btn').addEventListener('click', function() {
      appboyBridge.logClick(tracking[0][2]);
      try {
        if (button1[1].indexOf(0) !== -1) {
          appboyBridge.logClick('0');
        }
        if (button2[1].indexOf(0) !== -1) {
          appboyBridge.logClick('1');
        }
      } catch (err) {}
      window.location = deeplink[0];
    });

    // 3. X 버튼 클릭 (close_button) - 닫기
    // tracking: [3], button1/2: [2]
    document.querySelector('.close-btn').addEventListener('click', function() {
      appboyBridge.logClick(tracking[0][3]);
      try {
        if (button1[2].indexOf(0) !== -1) {
          appboyBridge.logClick('0');
        }
        if (button2[2].indexOf(0) !== -1) {
          appboyBridge.logClick('1');
        }
      } catch (err) {}
      appboyBridge.closeMessage();
    });

    // 4. 모달 배경 클릭 (background) - 모달 닫기
    // tracking: [0], button1/2: 없음 (background는 TEMPLATES_TRACKING_EDITOR에 없음)
    document.getElementById('modalBackground').addEventListener('click', function(e) {
      if (e.target === e.currentTarget) {
        appboyBridge.logClick(tracking[0][0]);
        appboyBridge.closeMessage();
      }
    });
  });
  `;

  return (
    <>
      <head>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta
          httpEquiv="Cache-Control"
          content="no-cache, no-store, must-revalidate"
        />
        <meta httpEquiv="Expires" content="0" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="stylesheet"
          as="style"
          href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdn.jsdelivr.net/npm/normalize.css@8.0.1/normalize.css"
        />
        <style dangerouslySetInnerHTML={{ __html: style }} />
      </head>
      <script
        dangerouslySetInnerHTML={{
          __html: `               
         (function () {
          function adjustFontSize() {
            var actualSize = Math.round(parseFloat(window.getComputedStyle(document.documentElement).fontSize));
            if (actualSize !== 16) {              
              var newFontSize = (16 * 16) / actualSize;
              document.documentElement.style.fontSize = newFontSize + "px";                            
            }
          }              

          // adjust font size before body load
              adjustFontSize();
              })();                           
          `
        }}
      />
      <body dangerouslySetInnerHTML={{ __html: html }} />
      <script dangerouslySetInnerHTML={{ __html: js }} />
    </>
  );
};

export default BasicFullRouletteHTML;
